import type { Cell, Workbook, Worksheet, CellValue } from "exceljs";

export type BorderSide = {
  style?: string;
  color?: string;
};

export type CellInfo = {
  sheet: string;
  row: number;
  col: number;
  address: string;
  value: string;
  fill?: string;
  fontName?: string;
  fontSize?: number;
  fontColor?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: "left" | "center" | "right";
  vAlign?: "top" | "middle" | "bottom";
  wrap?: boolean;
  border?: {
    top?: BorderSide;
    right?: BorderSide;
    bottom?: BorderSide;
    left?: BorderSide;
  };
};

export type SheetMeta = {
  name: string;
  merges: string[];
  colWidthsPx: number[];
  rowHeightsPx: number[];
  used: { minRow: number; maxRow: number; minCol: number; maxCol: number };
};

export type ReadXlsxCellsResult = {
  sheetNames: string[];
  sheet: string;
  merges: string[];
  meta: SheetMeta | null;
  cells: CellInfo[];
};

type WB = Workbook;

const bufferCache = new Map<string, Promise<ArrayBuffer>>();
const workbookCache = new Map<string, Promise<WB>>();
const sheetCache = new Map<string, Promise<ReadXlsxCellsResult>>();

function keyOf(url: string, sheetName?: string) {
  return `${url}::${sheetName || "__first__"}`;
}

async function fetchBufferOnce(url: string, fetchInit?: RequestInit) {
  let p = bufferCache.get(url);
  if (!p) {
    p = fetch(url, fetchInit).then(async (res) => {
      if (!res.ok) throw new Error(`Fetch xlsx failed: ${res.status}`);
      return await res.arrayBuffer();
    });
    bufferCache.set(url, p);
  }
  return p;
}

async function getWorkbookOnce(url: string, fetchInit?: RequestInit) {
  let p = workbookCache.get(url);
  
  if (!p) {
    p = (async () => {
      const buf = await fetchBufferOnce(url, fetchInit);
      
      const ExcelJSImport = await import("exceljs");
      const wb = new ExcelJSImport.Workbook();
      
      await wb.xlsx.load(buf);
      return wb;
    })();
    
    workbookCache.set(url, p);
  }
  
  return p;
}

export function clearXlsxCache(url?: string) {
  if (!url) {
    bufferCache.clear();
    workbookCache.clear();
    sheetCache.clear();
    return;
  }
  
  bufferCache.delete(url);
  workbookCache.delete(url);
  
  const keys = Array.from(sheetCache.keys());
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (k.startsWith(url + "::")) sheetCache.delete(k);
  }
}

type ExcelColor = { argb?: string };

type PatternFillLike = {
  fgColor?: ExcelColor;
  bgColor?: ExcelColor;
};

type ExcelFontLike = {
  name?: string;
  size?: number;
  color?: ExcelColor;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean | string;
};

type ExcelAlignmentLike = {
  horizontal?: string;
  vertical?: string;
  wrapText?: boolean;
};

type ExcelBorderSideLike = {
  style?: string;
  color?: ExcelColor;
};

type ExcelBorderLike = {
  top?: ExcelBorderSideLike;
  right?: ExcelBorderSideLike;
  bottom?: ExcelBorderSideLike;
  left?: ExcelBorderSideLike;
};

type WorksheetModelLike = {
  merges?: string[];
};

type WorksheetDimensionsLike = {
  top?: number;
  left?: number;
  bottom?: number;
  right?: number;
};

type WorksheetExtra = Worksheet & {
  model?: WorksheetModelLike;
  dimensions?: WorksheetDimensionsLike;
};

type RichTextValueLike = { richText: Array<{ text: string }> };
type FormulaValueLike = { formula: string; result?: CellValue };
type HyperlinkValueLike = { hyperlink: string; text?: string };
type TextValueLike = { text: string };

function argbToHex(argb?: string) {
  if (!argb) return undefined;
  if (argb.length === 8) return `#${argb.slice(2)}`;
  if (argb.length === 6) return `#${argb}`;
  return undefined;
}

function normalizeColor(c?: ExcelColor): string | undefined {
  if (!c?.argb) return undefined;
  return argbToHex(c.argb);
}

function safeFillToHex(cell: Cell): string | undefined {
  const fill = cell.fill as PatternFillLike | null | undefined;
  if (!fill) return undefined;
  
  const fg = normalizeColor(fill.fgColor);
  if (fg) return fg;
  
  const bg = normalizeColor(fill.bgColor);
  if (bg) return bg;
  
  return undefined;
}

function formatValue(v: CellValue): string {
  if (v == null) return "";
  
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (v instanceof Date) return v.toLocaleDateString();

  if (typeof v === "object" && v !== null && "richText" in v) {
    const rt = v as RichTextValueLike;
    return rt.richText.map((x) => x.text).join("");
  }

  if (typeof v === "object" && v !== null && "formula" in v) {
    const fv = v as FormulaValueLike;
    return fv.result != null ? formatValue(fv.result) : "";
  }

  if (typeof v === "object" && v !== null && "hyperlink" in v) {
    const hv = v as HyperlinkValueLike;
    return hv.text ?? hv.hyperlink ?? "";
  }

  if (typeof v === "object" && v !== null && "text" in v) {
    const tv = v as TextValueLike;
    return tv.text ?? "";
  }
  
  return String(v);
}

function colWidthToPx(width?: number) {
  if (!width) return 100;
  return Math.round(width * 7 + 16);
}

function rowHeightToPx(height?: number) {
  if (!height) return 20;
  return Math.round(height * 1.333);
}

function pickHAlign(h?: string): "left" | "center" | "right" {
  if (h === "center") return "center";
  if (h === "right") return "right";
  return "left";
}

function pickVAlign(v?: string): "top" | "middle" | "bottom" {
  if (v === "top") return "top";
  if (v === "bottom") return "bottom";
  return "middle";
}

function borderSideToInfo(side?: ExcelBorderSideLike): BorderSide | undefined {
  if (!side?.style) return undefined;
  return {
    style: String(side.style),
    color: normalizeColor(side.color),
  };
}

async function readXlsxCellsUnsafe(wb: WB, sheetName?: string): Promise<ReadXlsxCellsResult> {
  const sheetNames = wb.worksheets.map((w) => w.name);
  const wsBase = sheetName ? wb.getWorksheet(sheetName) : wb.worksheets[0];
  
  if (!wsBase) {
    return { sheetNames, sheet: "", merges: [], meta: null, cells: [] };
  }
  
  const ws = wsBase as WorksheetExtra;
  
  const merges = Array.isArray(ws.model?.merges) ? ws.model!.merges! : [];

  const dims = ws.dimensions;
  
  let minRow = 1,
    maxRow = 1,
    minCol = 1,
    maxCol = 1;
  
  if (
    dims &&
    typeof dims.top === "number" &&
    typeof dims.left === "number" &&
    typeof dims.bottom === "number" &&
    typeof dims.right === "number"
  ) {
    minRow = Math.max(1, dims.top);
    maxRow = Math.max(minRow, dims.bottom);
    minCol = Math.max(1, dims.left);
    maxCol = Math.max(minCol, dims.right);
  } else {
    maxRow = ws.actualRowCount || ws.rowCount || 1;
    maxCol = ws.actualColumnCount || ws.columnCount || 1;
  }

  if (merges.length) {
    const colToNumber = (col: string) => {
      let n = 0;
      for (let i = 0; i < col.length; i++) n = n * 26 + (col.charCodeAt(i) - 64);
      return n;
    };
    const decode = (addr: string) => {
      const m = addr.match(/^([A-Z]+)(\d+)$/);
      if (!m) return { c: 1, r: 1 };
      return { c: colToNumber(m[1]), r: Number(m[2]) };
    };
    
    for (const rng of merges) {
      const [a, b] = String(rng).split(":");
      if (!a || !b) continue;
      
      const s = decode(a);
      const e = decode(b);
      
      minRow = Math.min(minRow, s.r);
      minCol = Math.min(minCol, s.c);
      maxRow = Math.max(maxRow, e.r);
      maxCol = Math.max(maxCol, e.c);
    }
  }

  const colWidthsPx: number[] = [];
  for (let c = minCol; c <= maxCol; c++) {
    colWidthsPx.push(colWidthToPx(ws.getColumn(c).width));
  }

  const rowHeightsPx: number[] = [];
  for (let r = minRow; r <= maxRow; r++) {
    rowHeightsPx.push(rowHeightToPx(ws.getRow(r).height));
  }

  const cells: CellInfo[] = [];
  
  for (let r = minRow; r <= maxRow; r++) {
    const row = ws.getRow(r);
    
    for (let c = minCol; c <= maxCol; c++) {
      const cell = row.getCell(c);
      
      const value = cell.value;
      const hasValue = value != null && value !== "";
      
      const hasStyle =
        Boolean(cell.fill) || Boolean(cell.font) || Boolean(cell.alignment) || Boolean(cell.border);
      
      if (!hasValue && !hasStyle) continue;
      
      const font = cell.font as ExcelFontLike | null | undefined;
      const alignment = cell.alignment as ExcelAlignmentLike | null | undefined;
      const border = cell.border as ExcelBorderLike | null | undefined;
      
      cells.push({
        sheet: ws.name,
        row: r,
        col: c,
        address: cell.address,
        value: formatValue(value),
        fill: safeFillToHex(cell),
        fontName: font?.name,
        fontSize: font?.size,
        fontColor: normalizeColor(font?.color),
        bold: Boolean(font?.bold),
        italic: Boolean(font?.italic),
        underline: Boolean(font?.underline),
        align: pickHAlign(alignment?.horizontal),
        vAlign: pickVAlign(alignment?.vertical),
        wrap: Boolean(alignment?.wrapText),
        border: border
          ? {
            top: borderSideToInfo(border.top),
            right: borderSideToInfo(border.right),
            bottom: borderSideToInfo(border.bottom),
            left: borderSideToInfo(border.left),
          }
          : undefined,
      });
    }
  }
  
  const meta: SheetMeta = {
    name: ws.name,
    merges,
    colWidthsPx,
    rowHeightsPx,
    used: { minRow, maxRow, minCol, maxCol },
  };
  
  return { sheetNames, sheet: ws.name, merges, meta, cells };
}

export async function readXlsxCells(
  url: string,
  opts?: {
    sheetName?: string;
    fetchInit?: RequestInit;
    useSheetCache?: boolean;
  }
): Promise<ReadXlsxCellsResult> {
  const wb = await getWorkbookOnce(url, opts?.fetchInit);
  
  if (opts?.useSheetCache === false) {
    return await readXlsxCellsUnsafe(wb, opts?.sheetName);
  }
  
  const k = keyOf(url, opts?.sheetName);
  
  let p = sheetCache.get(k);
  if (!p) {
    p = readXlsxCellsUnsafe(wb, opts?.sheetName);
    sheetCache.set(k, p);
  }
  
  return await p;
}
